import type { ReactNode } from 'react';

/**
 * Shared diagram wrapper.
 *
 * Used by all in-lesson visuals (hand-authored SVG, Mermaid-rendered SVG,
 * React Flow canvases) so they share a single visual language: figure number,
 * caption, dark surface, consistent vertical rhythm, accessible labelling.
 *
 * Usage:
 *   <Diagram
 *     figure="1.2"
 *     title="The token-prediction loop"
 *     caption="The model predicts one token at a time, adds it to the input, and starts over."
 *     ariaLabel="A circular flow: input text leads to transformer, transformer outputs a probability distribution, sampling picks one token, the picked token is added back to the input, and the loop repeats."
 *   >
 *     <svg ... />  // or any visual content
 *   </Diagram>
 */
export function Diagram({
  figure,
  title,
  caption,
  ariaLabel,
  children,
  className = '',
}: {
  figure: string;
  title: string;
  caption?: string;
  ariaLabel: string;
  children: ReactNode;
  className?: string;
}) {
  return (
    <figure
      role="group"
      aria-label={ariaLabel}
      className={`my-8 overflow-hidden rounded-xl border border-line bg-elevated ${className}`}
    >
      <figcaption className="border-b border-line bg-surface px-5 py-3">
        <div className="flex flex-wrap items-baseline gap-x-3 gap-y-1">
          <span className="font-mono text-[10px] uppercase tracking-widest text-amber">
            Figure {figure}
          </span>
          <span className="text-sm font-semibold text-text">{title}</span>
        </div>
        {caption && (
          <p className="mt-1 text-[13px] text-muted">{caption}</p>
        )}
      </figcaption>
      <div className="p-5 sm:p-6 md:p-8">{children}</div>
    </figure>
  );
}
